import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Text, xcss } from '@atlaskit/primitives';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { ACTION_SUBJECT_PREFIX } from '../../../../common/constants/analytics';
import { ApplicationModes } from '../../../../common/constants/application-modes';
import { ResultsStates } from '../../../../common/constants/results';
import { useAppContext } from '../../../../common/ui/app-context';
import { Link } from '../../../../common/ui/link-component';
import { sha256Hash } from '../../../../common/utils/events/utils';
import { isHello } from '../../../../common/utils/is-hello';
import { isEnglishLocale } from '../../../../common/utils/locale';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../controllers/analytics';
import ShowIfEligible from '../../../../controllers/application-modes';
import { useAreLLMPoweredFeaturesEnabled } from '../../../../controllers/entitlement-checks';
import { useFetchAutoCorrection } from '../../../../controllers/results';
import { useBootstrap } from '../../../../controllers/store/bootstrap';
import { useOriginalQuery, useQueryInfo, useResultsState } from '../../../../controllers/store/search';

const anchorStyles = xcss({
	color: 'color.link',
	textDecoration: 'none',

	':hover': {
		textDecoration: 'underline',
	},
	':active': {
		textDecoration: 'none',
	},
	':visited': {
		color: 'color.link.visited',
	},
	':visited:active': {
		color: 'color.link.visited.pressed',
	},
});

const useFireClickEvent = () => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes } = useCommonAndPrivateAttributes();
	const [{ cloudId }] = useBootstrap();

	return (additionalAttributes?: Record<string, any>) => {
		// Check if this is a hello tenant - only hello tenants can pass sensitive fields
		const isHelloTenant: boolean = isHello(cloudId);

		let filteredAttributes = { ...additionalAttributes };
		if (!isHelloTenant) {
			// For non-hello tenants, replace fields with values to [REDACTED], keep null as null
			filteredAttributes = {
				...filteredAttributes,
				originalQuery: additionalAttributes?.originalQuery ? '[REDACTED]' : null,
				replacementQuery: additionalAttributes?.replacementQuery ? '[REDACTED]' : null,
				suggestedQuery: additionalAttributes?.suggestedQuery ? '[REDACTED]' : null,
			};
		}

		fireAnalyticsEvent({
			eventType: 'ui',
			actionSubject: `${ACTION_SUBJECT_PREFIX}AutoCorrection`,
			action: 'clicked',
			attributes: {
				commonAttributes,
				additionalAttributes: filteredAttributes,
			},
		});
	};
};

const useFireShownEvent = () => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes } = useCommonAndPrivateAttributes();
	const [{ cloudId }] = useBootstrap();

	return (additionalAttributes?: Record<string, any>) => {
		// Check if this is a hello tenant - only hello tenants can pass sensitive fields
		const isHelloTenant: boolean = isHello(cloudId);

		let filteredAttributes = { ...additionalAttributes };
		if (!isHelloTenant) {
			// For non-hello tenants, replace fields with values to [REDACTED], keep null as null
			filteredAttributes = {
				...filteredAttributes,
				originalQuery: additionalAttributes?.originalQuery ? '[REDACTED]' : null,
				replacementQuery: additionalAttributes?.replacementQuery ? '[REDACTED]' : null,
				suggestedQuery: additionalAttributes?.suggestedQuery ? '[REDACTED]' : null,
			};
		}

		fireAnalyticsEvent({
			eventType: 'ui',
			actionSubject: `${ACTION_SUBJECT_PREFIX}AutoCorrection`,
			action: 'shown',
			attributes: {
				commonAttributes,
				additionalAttributes: filteredAttributes,
			},
		});
	};
};



const AutoCorrection = () => {
	useFetchAutoCorrection();
	const { queryParams } = useAppContext();
	const [originalQueryUrlParam] = useOriginalQuery();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [queryInfo] = useQueryInfo();
	const fireClickEvent = useFireClickEvent();
	const fireShownEvent = useFireShownEvent();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const resultsState = useResultsState();
	const hasTrackedShown = useRef<string | false>(false);

	const { generateSearchUrl } = useAppContext();

	const shouldShowAutoCorrection =
		resultsState === ResultsStates.NO_RESULTS || resultsState === ResultsStates.RESULTS;

	// Fire shown events when auto-correction is displayed
	useEffect(() => {
		if (fg('rovo-search-spelling-correction')) {
			const {
				originalQuery,
				replacementQuery,
				suggestedQuery,
				originalQueryType,
				confidenceScore,
				shouldTriggerAutocorrectionExperience,
			} = queryInfo || {};

			const showAutoCorrection =
				(replacementQuery || suggestedQuery) && shouldTriggerAutocorrectionExperience;

			if (showAutoCorrection) {
				// Generate a unique key for the current auto-correction state
				// Only depend on the actual auto-correction data, not URL parameters
				const autoCorrectionKey = `${replacementQuery || ''}-${suggestedQuery || ''}`;

				if (hasTrackedShown.current !== autoCorrectionKey) {
					hasTrackedShown.current = autoCorrectionKey;

					if (!originalQueryUrlParam && replacementQuery) {
						// Fire shown events for replacement and original
						fireShownEvent({
							autoCorrectionType: 'replacement',
							originalQueryType,
							confidenceScore,
							originalQuery,
							replacementQuery,
							suggestedQuery: null,
							originalQueryHash: originalQuery ? sha256Hash(originalQuery) : null,
							replacementQueryHash: replacementQuery ? sha256Hash(replacementQuery) : null,
							suggestedQueryHash: null,
						});
						fireShownEvent({
							autoCorrectionType: 'original',
							originalQueryType,
							confidenceScore,
							originalQuery,
							replacementQuery,
							suggestedQuery: null,
							originalQueryHash: originalQuery ? sha256Hash(originalQuery) : null,
							replacementQueryHash: replacementQuery ? sha256Hash(replacementQuery) : null,
							suggestedQueryHash: null,
						});
					}

					if (!replacementQuery && suggestedQuery) {
						// Fire shown event for suggested
						fireShownEvent({
							autoCorrectionType: 'suggested',
							originalQueryType,
							confidenceScore,
							originalQuery,
							replacementQuery: null,
							suggestedQuery,
							originalQueryHash: originalQuery ? sha256Hash(originalQuery) : null,
							replacementQueryHash: null,
							suggestedQueryHash: suggestedQuery ? sha256Hash(suggestedQuery) : null,
						});
					}
				}
			}
		}
	}, [queryInfo, fireShownEvent, originalQueryUrlParam]);

	if (!shouldShowAutoCorrection) {
		return null;
	}

	if (fg('rovo-search-spelling-correction')) {
		const {
			originalQuery,
			replacementQuery,
			suggestedQuery,
			originalQueryType,
			confidenceScore,
			shouldTriggerAutocorrectionExperience,
		} = queryInfo || {};

		/**
		 * Show auto correction if:
		 * - replacementQuery is available
		 * - shouldTriggerAutocorrectionExperience is true (currently not available)
		 */
		const showAutoCorrection =
			(replacementQuery || suggestedQuery) && shouldTriggerAutocorrectionExperience;

		if (showAutoCorrection) {
			if (!originalQueryUrlParam && replacementQuery) {
				return (
					<Box>
						<Box>
							{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
							<Text size="large" weight="medium">
								Showing results for{' '}
								<Link
									href={generateSearchUrl({
										...queryParams,
										text: replacementQuery,
										originalQuery,
									})}
									xcss={anchorStyles}
									onClick={() =>
										fireClickEvent({
											autoCorrectionType: 'replacement',
											originalQueryType,
											confidenceScore,
											originalQuery,
											replacementQuery,
											suggestedQuery: null,
											originalQueryHash: originalQuery ? sha256Hash(originalQuery) : null,
											replacementQueryHash: replacementQuery ? sha256Hash(replacementQuery) : null,
											suggestedQueryHash: null,
										})
									}
								>
									<Text as="em" size="large" weight="medium" color="color.link">
										{replacementQuery}
									</Text>
								</Link>
							</Text>
						</Box>
						<Box>
							{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
							<Text>
								Search instead for{' '}
								<Link
									href={generateSearchUrl({
										...queryParams,
										text: originalQuery,
										originalQuery: replacementQuery,
									})}
									xcss={anchorStyles}
									onClick={() =>
										fireClickEvent({
											autoCorrectionType: 'original',
											originalQueryType,
											confidenceScore,
											originalQuery,
											replacementQuery,
											suggestedQuery: null,
											originalQueryHash: originalQuery ? sha256Hash(originalQuery) : null,
											replacementQueryHash: replacementQuery ? sha256Hash(replacementQuery) : null,
											suggestedQueryHash: null,
										})
									}
								>
									<Text as="em" color="color.link">
										{originalQuery}
									</Text>
								</Link>
							</Text>
						</Box>
					</Box>
				);
			}

			if (!replacementQuery && suggestedQuery) {
				return (
					<Box>
						{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
						<Text size="large" weight="medium">
							Did you mean{' '}
							<Link
								// TODO: Why changing the query using query params and not Store actions?
								href={generateSearchUrl({
									...queryParams,
									text: suggestedQuery,
									originalQuery: originalQuery,
								})}
								xcss={anchorStyles}
								onClick={() =>
									fireClickEvent({
										autoCorrectionType: 'suggested',
										originalQueryType,
										confidenceScore,
										originalQuery,
										replacementQuery: null,
										suggestedQuery,
										originalQueryHash: originalQuery ? sha256Hash(originalQuery) : null,
										replacementQueryHash: null,
										suggestedQueryHash: suggestedQuery ? sha256Hash(suggestedQuery) : null,
									})
								}
							>
								<Text as="em" size="large" weight="medium" color="color.link">
									{suggestedQuery}
								</Text>
							</Link>
						</Text>
					</Box>
				);
			}
		}
	}

	return null;
};

const AutoCorrectionIfEligible = (): React.JSX.Element | null => {
	const { locale } = useIntl();
	const areLLMFeaturesEnabled = useAreLLMPoweredFeaturesEnabled();
	if (!isEnglishLocale(locale) || !areLLMFeaturesEnabled) {
		return null;
	}
	return (
		<ShowIfEligible requiredMode={ApplicationModes.Rovo}>
			<UFOSegment name="search-page-auto-correction">
				<AutoCorrection />
			</UFOSegment>
		</ShowIfEligible>
	);
};

export default AutoCorrectionIfEligible;
